import { Job } from '../types';

// Cache for scraped jobs data
let scrapedJobsCache: any = null;

// Fetch scraped jobs data
const fetchScrapedJobs = async (): Promise<any> => {
  if (scrapedJobsCache) {
    return scrapedJobsCache;
  }

  try {
    const response = await fetch('/scraped-jobs.json');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    scrapedJobsCache = data;
    console.log('Scraped jobs data fetched successfully:', data);
    return data;
  } catch (error) {
    console.warn('Could not fetch scraped-jobs.json:', error);
    // Return empty data structure
    return { jobs: [], lastUpdated: new Date().toISOString() };
  }
};

// Transform scraped jobs to match our Job interface
export const transformScrapedJobs = async (): Promise<Job[]> => {
  const scrapedJobsData = await fetchScrapedJobs();
  
  if (!scrapedJobsData.jobs || !Array.isArray(scrapedJobsData.jobs)) {
    console.warn('No scraped jobs found or invalid format');
    return [];
  }

  const transformedJobs = scrapedJobsData.jobs.map((scrapedJob: any) => ({
    id: scrapedJob.id || `scraped-${Math.random().toString(36).substr(2, 9)}`,
    title: scrapedJob.title || 'Untitled Position',
    company: scrapedJob.company || 'Unknown Company',
    location: Array.isArray(scrapedJob.location) ? scrapedJob.location : ['Location TBD'],
    description: scrapedJob.description || 'No description available',
    requirements: Array.isArray(scrapedJob.requirements) ? scrapedJob.requirements : [],
    salary: scrapedJob.salary || undefined,
    type: scrapedJob.type || 'full-time',
    remote: Boolean(scrapedJob.remote),
    scrapedDate: scrapedJob.scrapedDate || new Date().toISOString().split('T')[0],
    applicationUrl: scrapedJob.applicationUrl || '#',
    source: scrapedJob.source || 'Company Website',
    tags: Array.isArray(scrapedJob.tags) ? scrapedJob.tags : [],
    contentHash: scrapedJob.contentHash || '',
  }));
  
  console.log('Transformed jobs:', transformedJobs);
  return transformedJobs;
};

// Get all available jobs (sample + scraped)
export const getAllJobs = async (): Promise<Job[]> => {
  const scrapedJobs = await transformScrapedJobs();
  return scrapedJobs;
};

// Get job statistics
export const getJobStats = async () => {
  const scrapedJobs = await transformScrapedJobs();
  const totalJobs = scrapedJobs.length;
  
  // Count by company
  const companyCounts: { [key: string]: number } = {};
  scrapedJobs.forEach(job => {
    companyCounts[job.company] = (companyCounts[job.company] || 0) + 1;
  });

  // Count by type
  const typeCounts: { [key: string]: number } = {};
  scrapedJobs.forEach(job => {
    typeCounts[job.type] = (typeCounts[job.type] || 0) + 1;
  });

  // Count remote vs on-site
  const remoteCount = scrapedJobs.filter(job => job.remote).length;
  const onSiteCount = totalJobs - remoteCount;

  // Get unique tags
  const allTags = new Set<string>();
  scrapedJobs.forEach(job => {
    job.tags.forEach(tag => allTags.add(tag));
  });

  const stats = {
    totalJobs,
    companyCounts,
    typeCounts,
    remoteCount,
    onSiteCount,
    uniqueTags: Array.from(allTags),
    lastUpdated: scrapedJobsCache?.lastUpdated || new Date().toISOString()
  };

  console.log('Job stats calculated:', stats);
  return stats;
};

// Get unique companies for filtering
export const getUniqueCompanies = async (): Promise<string[]> => {
  const scrapedJobs = await transformScrapedJobs();
  const companies = new Set<string>();
  scrapedJobs.forEach(job => companies.add(job.company));
  return Array.from(companies).sort();
};

// Get unique locations for filtering
export const getUniqueLocations = async (): Promise<string[]> => {
  const scrapedJobs = await transformScrapedJobs();
  const locations = new Set<string>();
  
  scrapedJobs.forEach(job => {
    if (job.location && job.location.length > 0) {
      for (const loc of job.location) {
        // Skip blank or empty locations
        if (!loc || loc.trim() === '' || loc === 'N/A') {
          continue;
        }
        
        // Extract country from location string
        const parts = loc.split(', ');
        const country = parts[parts.length - 1]; // Last part is country
        
        // Skip if country is blank or N/A
        if (!country || country.trim() === '' || country === 'N/A') {
          continue;
        }
        
        if (job.remote) {
          // For remote jobs, group by country
          locations.add(`Remote (${country})`);
        } else {
          // For non-remote jobs, add the full location
          locations.add(loc);
        }
      }
    }
  });
  
  return ['All Locations', ...Array.from(locations).sort()];
};

// Get all unique tags for filtering
export const getAllUniqueTags = async (): Promise<string[]> => {
  const scrapedJobs = await transformScrapedJobs();
  const tags = new Set<string>();
  scrapedJobs.forEach(job => {
    job.tags.forEach(tag => tags.add(tag));
  });
  return Array.from(tags).sort();
};
